// API Client for Gateway
// All API calls go through the gateway in production

import type {
  AuthLoginResponse,
  AuthRefreshResponse,
  UserProfile,
  ActivityLog,
  UserSession,
  ManagedUser,
  Invitation,
  CompanyRecord,
  FacilityRecord,
  FacilityStats,
  EmissionRecordResponse,
  EmissionCalculationResult,
  EmissionFactorRecord,
  ESGMetricRecord,
  ESGMetricSummary,
  ReportRecord,
  ReportGenerateResponse,
  WaterRecordResponse,
  WaterSummary,
  WaterFacilitySummary,
  GreyWaterFootprintResult,
  PlanCode,
  SubscriptionStatus,
  PlanRecord,
  SubscriptionRecord,
  CheckoutConfig,
  ContactRequest,
  ContactResponse,
} from '../types/apiResponses';
import type {
  ComplianceStandard,
  ComplianceEvaluation,
  ComplianceEvaluationDetail,
  CreateEvaluationResponse,
} from '../types/compliance';
import type {
  GoldenReport,
  GoldenReportText,
  GoldenLabel,
  GoldenReportDetail,
  GoldenRevealResult,
  SaveLabelInput,
} from '../types/golden';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';
const REQUEST_TIMEOUT = 30000; // 30 seconds
const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 1000; // 1 second

interface ApiError {
  code: string;
  message: string;
  details?: unknown;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: ApiError;
}

class NetworkError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NetworkError';
  }
}

class TimeoutError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TimeoutError';
  }
}

class ApiClientError extends Error {
  code: string;
  statusCode?: number;

  constructor(message: string, code: string, statusCode?: number) {
    super(message);
    this.name = 'ApiClientError';
    this.code = code;
    this.statusCode = statusCode;
  }
}

class ApiClient {
  private baseUrl: string;
  private accessToken: string | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  setAccessToken(token: string | null) {
    this.accessToken = token;
  }

  private async sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private shouldRetry(error: Error, attempt: number, effectiveMaxRetries: number): boolean {
    if (attempt >= effectiveMaxRetries) return false;

    // Retry on network errors
    if (error instanceof NetworkError) return true;

    // Retry on timeout errors
    if (error instanceof TimeoutError) return true;

    // Retry on 5xx server errors
    if (error instanceof ApiClientError && error.statusCode && error.statusCode >= 500) {
      return true;
    }

    // Retry on specific error codes
    if (error instanceof ApiClientError) {
      const retryableCodes = ['NETWORK_ERROR', 'TIMEOUT', 'SERVICE_UNAVAILABLE'];
      return retryableCodes.includes(error.code);
    }

    return false;
  }

  private getRetryDelay(attempt: number): number {
    // Exponential backoff: 1s, 2s, 4s
    return INITIAL_RETRY_DELAY * Math.pow(2, attempt);
  }

  private async requestWithTimeout(
    url: string,
    options: RequestInit,
    timeout: number
  ): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === 'AbortError') {
        throw new TimeoutError(`Request timeout after ${timeout}ms`);
      }
      throw error;
    }
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    options?: RequestInit & { retries?: number; timeout?: number },
    cfg?: { timeoutMs?: number; maxRetries?: number }
  ): Promise<T> {
    const effectiveMaxRetries = cfg?.maxRetries ?? options?.retries ?? MAX_RETRIES;
    const effectiveTimeout = cfg?.timeoutMs ?? options?.timeout ?? REQUEST_TIMEOUT;
    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= effectiveMaxRetries; attempt++) {
      try {
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
          ...((options?.headers as Record<string, string>) || {}),
        };

        if (this.accessToken) {
          headers['Authorization'] = `Bearer ${this.accessToken}`;
        }

        const response = await this.requestWithTimeout(
          `${this.baseUrl}${path}`,
          {
            method,
            headers,
            body: body ? JSON.stringify(body) : undefined,
            ...options,
          },
          effectiveTimeout
        );

        // Handle non-JSON responses
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          if (!response.ok) {
            throw new ApiClientError(
              `HTTP ${response.status}: ${response.statusText}`,
              'HTTP_ERROR',
              response.status
            );
          }
          // Return empty object for successful non-JSON responses
          return {} as T;
        }

        const data: ApiResponse<T> = await response.json();

        if (!response.ok || !data.success) {
          throw new ApiClientError(
            data.error?.message || `HTTP ${response.status}: ${response.statusText}`,
            data.error?.code || 'API_ERROR',
            response.status
          );
        }

        return data.data as T;
      } catch (error) {
        lastError = error instanceof Error ? error : new Error('Unknown error');

        // Check if we should retry
        if (!this.shouldRetry(lastError, attempt, effectiveMaxRetries)) {
          break;
        }

        // Don't sleep after last attempt
        if (attempt < effectiveMaxRetries) {
          const delay = this.getRetryDelay(attempt);
          await this.sleep(delay);
        }
      }
    }

    // Re-throw the last error
    if (lastError) {
      throw lastError;
    }

    throw new Error('Request failed with unknown error');
  }

  get<T>(path: string, options?: RequestInit): Promise<T> {
    return this.request<T>('GET', path, undefined, options);
  }

  post<T>(path: string, body?: unknown, options?: RequestInit, cfg?: { timeoutMs?: number; maxRetries?: number }): Promise<T> {
    return this.request<T>('POST', path, body, options, cfg);
  }

  put<T>(path: string, body?: unknown, options?: RequestInit): Promise<T> {
    return this.request<T>('PUT', path, body, options);
  }

  patch<T>(path: string, body?: unknown, options?: RequestInit): Promise<T> {
    return this.request<T>('PATCH', path, body, options);
  }

  delete<T>(path: string, options?: RequestInit): Promise<T> {
    return this.request<T>('DELETE', path, undefined, options);
  }

  // Upload file to R2 via presigned URL
  async uploadFile(
    file: File,
    folder: string = 'evidence',
    emissionRecordId?: string
  ): Promise<{ id: string; fileName: string }> {
    // 1. Get presigned upload URL
    const { uploadUrl, fileKey } = await this.post<{
      uploadUrl: string;
      fileKey: string;
      expiresIn: number;
    }>('/api/v1/storage/presigned-upload', {
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
      folder,
      emissionRecordId,
    });

    // 2. Upload directly to R2
    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });

    if (!uploadResponse.ok) {
      throw new Error('Failed to upload file to storage');
    }

    // 3. Confirm upload and save metadata
    const result = await this.post<{ id: string; fileName: string }>(
      '/api/v1/storage/confirm-upload',
      {
        fileKey,
        emissionRecordId,
      }
    );

    return result;
  }

  // Get download URL for evidence
  async getDownloadUrl(evidenceId: string): Promise<string> {
    const { downloadUrl } = await this.get<{ downloadUrl: string }>(
      `/api/v1/storage/evidence/${evidenceId}/download`
    );
    return downloadUrl;
  }

  // ============================================
  // AUTH API
  // ============================================

  auth = {
    login: (data: { email: string; password: string }) =>
      this.post<AuthLoginResponse>('/api/v1/auth/login', data),

    signUp: (data: {
      email: string;
      password: string;
      companyName?: string;
      firstName?: string;
      lastName?: string;
      invitationToken?: string;
    }) => this.post<AuthLoginResponse>('/api/v1/auth/signup', data),

    refreshToken: (data: { refreshToken: string }) =>
      this.post<AuthRefreshResponse>('/api/v1/auth/refresh', data),

    logout: () => this.post<void>('/api/v1/auth/logout'),

    acceptInvitation: (data: { token: string; password: string; firstName?: string; lastName?: string }) =>
      this.post<AuthLoginResponse>('/api/v1/auth/accept-invitation', data),

    requestPasswordReset: (data: { email: string }) =>
      this.post<void>('/api/v1/auth/forgot-password', data),

    resetPassword: (data: { token: string; newPassword: string }) =>
      this.post<void>('/api/v1/auth/reset-password', data),
  };

  // ============================================
  // PROFILE API
  // ============================================

  profile = {
    get: () => this.get<UserProfile>('/api/v1/profile'),

    update: (data: { first_name?: string; last_name?: string }) =>
      this.patch<UserProfile>('/api/v1/profile', data),

    changePassword: (data: { currentPassword: string; newPassword: string }) =>
      this.patch<void>('/api/v1/profile/password', data),

    getActivity: (params?: { limit?: number; offset?: number }) => {
      const query = new URLSearchParams();
      if (params?.limit) query.append('limit', params.limit.toString());
      if (params?.offset) query.append('offset', params.offset.toString());
      return this.get<ActivityLog[]>(`/api/v1/profile/activity?${query.toString()}`);
    },

    getSessions: () => this.get<UserSession[]>('/api/v1/profile/sessions'),

    revokeSession: (sessionId: string) =>
      this.delete<void>(`/api/v1/profile/sessions/${sessionId}`),
  };

  // ============================================
  // USER MANAGEMENT API
  // ============================================

  users = {
    list: () => this.get<ManagedUser[]>('/api/v1/users'),

    invite: (data: { email: string; role: 'admin' | 'manager' | 'data_entry' | 'auditor' }) =>
      this.post<Invitation>('/api/v1/users/invite', data),

    updateRole: (userId: string, data: { role: 'admin' | 'manager' | 'data_entry' | 'auditor' }) =>
      this.patch<ManagedUser>(`/api/v1/users/${userId}/role`, data),

    deactivate: (userId: string) =>
      this.patch<ManagedUser>(`/api/v1/users/${userId}/deactivate`),

    getInvitations: () => this.get<Invitation[]>('/api/v1/users/invitations'),

    cancelInvitation: (invitationId: string) =>
      this.delete<void>(`/api/v1/users/invitations/${invitationId}`),
  };

  // ============================================
  // COMPANIES API
  // ============================================

  companies = {
    list: () => this.get<CompanyRecord[]>('/api/v1/companies'),

    get: (id: string) => this.get<CompanyRecord>(`/api/v1/companies/${id}`),

    getDetails: (id: string) => this.get<CompanyRecord>(`/api/v1/companies/${id}/details`),

    create: (data: {
      name: string;
      industry_sector?: string;
      employee_count?: number;
      base_year?: number;
      country?: string;
    }) => this.post<CompanyRecord>('/api/v1/companies', data),

    update: (id: string, data: Partial<{
      name: string;
      industry_sector: string;
      employee_count: number;
      base_year: number;
      country: string;
    }>) => this.put<CompanyRecord>(`/api/v1/companies/${id}`, data),

    delete: (id: string) => this.delete<void>(`/api/v1/companies/${id}`),
  };

  // ============================================
  // FACILITIES API
  // ============================================

  facilities = {
    list: (companyId?: string) => {
      const query = companyId ? `?company_id=${companyId}` : '';
      return this.get<FacilityRecord[]>(`/api/v1/facilities${query}`);
    },

    get: (id: string) => this.get<FacilityRecord>(`/api/v1/facilities/${id}`),

    getStats: (id: string) => this.get<FacilityStats>(`/api/v1/facilities/${id}/stats`),

    create: (data: {
      company_id: string;
      name: string;
      country: string;
      city?: string;
      address?: string;
      facility_type: 'factory' | 'office' | 'warehouse' | 'retail' | 'data_center' | 'other';
      operational_control?: boolean;
      equity_share?: number;
    }) => this.post<FacilityRecord>('/api/v1/facilities', data),

    update: (id: string, data: Partial<{
      name: string;
      country: string;
      city: string;
      address: string;
      facility_type: 'factory' | 'office' | 'warehouse' | 'retail' | 'data_center' | 'other';
      operational_control: boolean;
      equity_share: number;
    }>) => this.put<FacilityRecord>(`/api/v1/facilities/${id}`, data),

    delete: (id: string) => this.delete<void>(`/api/v1/facilities/${id}`),
  };

  // ============================================
  // EMISSIONS API
  // ============================================

  emissions = {
    list: (filters?: {
      facility_id?: string;
      scope?: number;
      from?: string;
      to?: string;
      deleted?: boolean;
    }) => {
      const params = new URLSearchParams();
      if (filters?.facility_id) params.append('facility_id', filters.facility_id);
      if (filters?.scope) params.append('scope', filters.scope.toString());
      if (filters?.from) params.append('from', filters.from);
      if (filters?.to) params.append('to', filters.to);
      if (filters?.deleted) params.append('deleted', filters.deleted.toString());

      const query = params.toString() ? `?${params.toString()}` : '';
      return this.get<EmissionRecordResponse[]>(`/api/v1/emissions${query}`);
    },

    get: (id: string) => this.get<EmissionRecordResponse>(`/api/v1/emissions/${id}`),

    getSummary: (facilityId: string, from?: string, to?: string) => {
      const params = new URLSearchParams();
      if (from) params.append('from', from);
      if (to) params.append('to', to);
      const query = params.toString() ? `?${params.toString()}` : '';

      return this.get<{
        scope1: number;
        scope2: number;
        scope3: number;
        total: number;
        count: number;
      }>(`/api/v1/emissions/facility/${facilityId}/summary${query}`);
    },

    calculate: (data: {
      activity_type: string;
      fuel_type?: string;
      amount: number;
      unit: string;
      region?: string;
      year?: number;
    }) => this.post<EmissionCalculationResult>('/api/v1/emissions/calculate', data),

    create: (data: {
      facility_id: string;
      scope: number;
      scope3_category?: number;
      activity_type: string;
      fuel_type?: string;
      amount: number;
      unit: string;
      period_start: string;
      period_end: string;
      emission_factor_id?: string;
      calculated_co2e?: number;
      calculated_co2?: number;
      calculated_ch4?: number;
      calculated_n2o?: number;
      data_quality_score?: number;
      notes?: string;
    }) => this.post<EmissionRecordResponse>('/api/v1/emissions', data),

    update: (id: string, data: Partial<{
      scope: number;
      scope3_category: number;
      activity_type: string;
      fuel_type: string;
      amount: number;
      unit: string;
      period_start: string;
      period_end: string;
      emission_factor_id: string;
      calculated_co2e: number;
      calculated_co2: number;
      calculated_ch4: number;
      calculated_n2o: number;
      data_quality_score: number;
      notes: string;
    }>) => this.put<EmissionRecordResponse>(`/api/v1/emissions/${id}`, data),

    delete: (id: string) => this.delete<void>(`/api/v1/emissions/${id}`),

    deletePermanent: (id: string) => this.delete<void>(`/api/v1/emissions/${id}/permanent`),
  };

  // ============================================
  // FACTORS API
  // ============================================

  factors = {
    list: (filters?: {
      source?: string;
      category?: string;
      region?: string;
      year?: number;
      search?: string;
    }) => {
      const params = new URLSearchParams();
      if (filters?.source) params.append('source', filters.source);
      if (filters?.category) params.append('category', filters.category);
      if (filters?.region) params.append('region', filters.region);
      if (filters?.year) params.append('year', filters.year.toString());
      if (filters?.search) params.append('search', filters.search);

      const query = params.toString() ? `?${params.toString()}` : '';
      return this.get<EmissionFactorRecord[]>(`/api/v1/factors${query}`);
    },

    get: (id: string) => this.get<EmissionFactorRecord>(`/api/v1/factors/${id}`),

    getCategories: () => this.get<string[]>('/api/v1/factors/meta/categories'),

    getRegions: () => this.get<string[]>('/api/v1/factors/meta/regions'),

    match: (data: {
      activityType: string;
      fuelType?: string;
      region: string;
      year: number;
      unitInput: string;
    }) => this.post<EmissionFactorRecord>('/api/v1/factors/match', data),
  };

  // ESG Metrics methods
  esgMetrics = {
    list: (params: {
      company_id: string;
      category?: 'environmental' | 'social' | 'governance';
      from?: string;
      to?: string;
    }) => {
      const query = new URLSearchParams();
      if (params.company_id) query.append('company_id', params.company_id);
      if (params.category) query.append('category', params.category);
      if (params.from) query.append('from', params.from);
      if (params.to) query.append('to', params.to);
      return this.get<ESGMetricRecord[]>(`/api/v1/esg-metrics?${query.toString()}`);
    },

    get: (id: string) => this.get<ESGMetricRecord>(`/api/v1/esg-metrics/${id}`),

    getSummary: (companyId: string, params?: { from?: string; to?: string }) => {
      const query = new URLSearchParams();
      if (params?.from) query.append('from', params.from);
      if (params?.to) query.append('to', params.to);
      const queryString = query.toString() ? `?${query.toString()}` : '';
      return this.get<ESGMetricSummary>(`/api/v1/esg-metrics/company/${companyId}/summary${queryString}`);
    },

    create: (data: {
      company_id: string;
      category: 'environmental' | 'social' | 'governance';
      metric_name: string;
      value: number;
      unit?: string;
      period_start: string;
      period_end: string;
      data_source?: string;
      verification_status?: 'unverified' | 'internal' | 'third_party';
      notes?: string;
    }) => this.post<ESGMetricRecord>('/api/v1/esg-metrics', data),

    update: (id: string, data: Partial<{
      category: 'environmental' | 'social' | 'governance';
      metric_name: string;
      value: number;
      unit?: string;
      period_start: string;
      period_end: string;
      data_source?: string;
      verification_status?: 'unverified' | 'internal' | 'third_party';
      notes?: string;
    }>) => this.put<ESGMetricRecord>(`/api/v1/esg-metrics/${id}`, data),

    delete: (id: string) => this.delete(`/api/v1/esg-metrics/${id}`),
  };

  // Reports methods
  reports = {
    list: (params: {
      company_id: string;
      report_type?: 'esg' | 'carbon' | 'sustainability' | 'custom';
      status?: 'draft' | 'in-progress' | 'published' | 'archived';
      standard?: string;
      from?: string;
      to?: string;
    }) => {
      const query = new URLSearchParams();
      if (params.company_id) query.append('company_id', params.company_id);
      if (params.report_type) query.append('report_type', params.report_type);
      if (params.status) query.append('status', params.status);
      if (params.standard) query.append('standard', params.standard);
      if (params.from) query.append('from', params.from);
      if (params.to) query.append('to', params.to);
      return this.get<ReportRecord[]>(`/api/v1/reports?${query.toString()}`);
    },

    get: (id: string) => this.get<ReportRecord>(`/api/v1/reports/${id}`),

    getRecent: (companyId: string, limit?: number) => {
      const query = new URLSearchParams();
      if (limit !== undefined) query.append('limit', limit.toString());
      const queryString = query.toString() ? `?${query.toString()}` : '';
      return this.get<ReportRecord[]>(`/api/v1/reports/company/${companyId}/recent${queryString}`);
    },

    // Binary download — bypasses the JSON request() helper entirely so the
    // response body can be read as a Blob instead of parsed as JSON.
    download: async (id: string): Promise<Blob> => {
      const headers: Record<string, string> = {};
      if (this.accessToken) {
        headers['Authorization'] = `Bearer ${this.accessToken}`;
      }

      const response = await fetch(`${this.baseUrl}/api/v1/reports/${id}/download`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        let message = `HTTP ${response.status}: ${response.statusText}`;
        let code = 'HTTP_ERROR';
        try {
          const data: ApiResponse<never> = await response.json();
          message = data.error?.message || message;
          code = data.error?.code || code;
        } catch {
          // Error response wasn't JSON — fall back to the status text above.
        }
        throw new ApiClientError(message, code, response.status);
      }

      return response.blob();
    },

    generate: (data: {
      company_id: string;
      report_type: 'esg' | 'carbon' | 'sustainability' | 'custom';
      standard: 'GRI' | 'SASB' | 'TCFD' | 'ISO14064' | 'GHG-Protocol' | 'CBAM' | 'Combined' | 'Custom';
      format: 'pdf' | 'xlsx' | 'json';
      period_start: string;
      period_end: string;
      facility_ids?: string[];
      scopes?: number[];
      language?: string;
    }) => this.post<ReportGenerateResponse>('/api/v1/reports/generate', data),

    create: (data: {
      company_id: string;
      name: string;
      report_type: 'esg' | 'carbon' | 'sustainability' | 'custom';
      standard?: string;
      format: 'pdf' | 'xlsx' | 'json';
      period_start: string;
      period_end: string;
      facility_ids?: string[];
      scopes?: number[];
      language?: string;
      status?: 'draft' | 'in-progress' | 'published' | 'archived';
      file_url?: string;
    }) => this.post<ReportRecord>('/api/v1/reports', data),

    update: (id: string, data: Partial<{
      name: string;
      report_type: 'esg' | 'carbon' | 'sustainability' | 'custom';
      standard?: string;
      format: 'pdf' | 'xlsx' | 'json';
      period_start: string;
      period_end: string;
      status: 'draft' | 'in-progress' | 'published' | 'archived';
      file_url?: string;
    }>) => this.put<ReportRecord>(`/api/v1/reports/${id}`, data),

    delete: (id: string) => this.delete(`/api/v1/reports/${id}`),

    deletePermanent: (id: string) => this.delete(`/api/v1/reports/${id}/permanent`),
  };

  water = {
    list: (filters?: {
      facility_id?: string;
      water_type?: 'blue' | 'green' | 'grey';
      source_type?: 'municipal' | 'groundwater' | 'surface' | 'rainwater' | 'recycled';
      from?: string;
      to?: string;
      deleted?: boolean;
    }) => {
      const params = new URLSearchParams();
      if (filters?.facility_id) params.append('facility_id', filters.facility_id);
      if (filters?.water_type) params.append('water_type', filters.water_type);
      if (filters?.source_type) params.append('source_type', filters.source_type);
      if (filters?.from) params.append('from', filters.from);
      if (filters?.to) params.append('to', filters.to);
      if (filters?.deleted) params.append('deleted', 'true');
      const query = params.toString();
      return this.get<WaterRecordResponse[]>(`/api/v1/water${query ? `?${query}` : ''}`);
    },

    get: (id: string) => this.get<WaterRecordResponse>(`/api/v1/water/${id}`),

    getSummary: (filters?: { from?: string; to?: string }) => {
      const params = new URLSearchParams();
      if (filters?.from) params.append('from', filters.from);
      if (filters?.to) params.append('to', filters.to);
      const query = params.toString();
      return this.get<WaterSummary>(`/api/v1/water/summary${query ? `?${query}` : ''}`);
    },

    getFacilitySummary: (facilityId: string, filters?: { from?: string; to?: string }) => {
      const params = new URLSearchParams();
      if (filters?.from) params.append('from', filters.from);
      if (filters?.to) params.append('to', filters.to);
      const query = params.toString();
      return this.get<WaterFacilitySummary>(`/api/v1/water/facility/${facilityId}/summary${query ? `?${query}` : ''}`);
    },

    create: (data: {
      facility_id: string;
      water_type: 'blue' | 'green' | 'grey';
      source_type: 'municipal' | 'groundwater' | 'surface' | 'rainwater' | 'recycled';
      record_type?: 'withdrawal' | 'discharge' | 'consumption';
      amount: number;
      unit?: string;
      period_start: string;
      period_end: string;
      discharge_destination?: 'surface_water' | 'groundwater' | 'seawater' | 'third_party' | 'other';
      discharge_treatment_level?: 'none' | 'primary' | 'secondary' | 'tertiary';
      water_stress_index?: number;
      aware_factor?: number;
      quality_cod?: number;
      quality_bod?: number;
      quality_tss?: number;
      quality_ph?: number;
      pollutant_load?: number;
      max_concentration?: number;
      natural_concentration?: number;
      notes?: string;
    }) => this.post<WaterRecordResponse>('/api/v1/water', data),

    update: (id: string, data: Partial<{
      water_type: 'blue' | 'green' | 'grey';
      source_type: 'municipal' | 'groundwater' | 'surface' | 'rainwater' | 'recycled';
      record_type: 'withdrawal' | 'discharge' | 'consumption';
      amount: number;
      unit: string;
      period_start: string;
      period_end: string;
      discharge_destination: 'surface_water' | 'groundwater' | 'seawater' | 'third_party' | 'other';
      discharge_treatment_level: 'none' | 'primary' | 'secondary' | 'tertiary';
      water_stress_index: number;
      aware_factor: number;
      quality_cod: number;
      quality_bod: number;
      quality_tss: number;
      quality_ph: number;
      pollutant_load: number;
      max_concentration: number;
      natural_concentration: number;
      notes: string;
    }>) => this.put<WaterRecordResponse>(`/api/v1/water/${id}`, data),

    calculateGreyFootprint: (data: {
      pollutant_load: number;
      max_concentration: number;
      natural_concentration: number;
    }) => this.post<GreyWaterFootprintResult>('/api/v1/water/calculate-grey-footprint', data),

    delete: (id: string) => this.delete(`/api/v1/water/${id}`),
  };

  // ============================================
  // COMPLIANCE API
  // ============================================

  compliance = {
    listStandards: () =>
      this.get<ComplianceStandard[]>('/api/v1/compliance/standards'),

    listEvaluations: () =>
      this.get<ComplianceEvaluation[]>('/api/v1/compliance/evaluations'),

    getEvaluation: (id: string) =>
      this.get<ComplianceEvaluationDetail>(`/api/v1/compliance/evaluations/${id}`),

    // The service starts the evaluation as a background job and answers 202 immediately
    // with just enough to start polling getEvaluation(id) — never the finished record.
    createEvaluation: (data: {
      company_id: string;
      standard_id: string;
      report_name: string;
      output_language: 'tr' | 'en';
      report_base64: string;
      report_file_id?: string;
    }) => this.post<CreateEvaluationResponse>('/api/v1/compliance/evaluations', data, undefined, { timeoutMs: 300000, maxRetries: 0 }),

    deleteEvaluation: (id: string) =>
      this.delete<void>(`/api/v1/compliance/evaluations/${id}`),
  };

  // ============================================
  // GOLDEN SET API
  // ============================================

  golden = {
    listReports: () => this.get<GoldenReport[]>('/api/v1/golden/reports'),

    getReport: (id: string) => this.get<GoldenReportDetail>(`/api/v1/golden/reports/${id}`),

    getReportText: (id: string) => this.get<GoldenReportText>(`/api/v1/golden/reports/${id}/text`),

    getPdfUrl: (id: string) => this.get<{ url: string }>(`/api/v1/golden/reports/${id}/pdf-url`),

    // Both extra fields are optional because the route has two shapes: a fresh upload
    // answers 201 with `pdf_stored`, while an identical PDF answers 200 with
    // `already_existed: true` and a bare report row that carries no `pdf_stored` at all.
    // Promising `pdf_stored: boolean` unconditionally would hand the upload form
    // `undefined` where a boolean was guaranteed, and would hide the only signal that the
    // upload was a no-op.
    createReport: (data: { slug: string; company_name: string; report_year?: number; standard_id: string; expected_quality: 'strong' | 'medium' | 'weak'; report_base64: string }) =>
      this.post<GoldenReport & { pdf_stored?: boolean; already_existed?: boolean }>('/api/v1/golden/reports', data, undefined, { timeoutMs: 120000, maxRetries: 0 }),

    saveLabel: ({ reportId, disclosureId, ...body }: SaveLabelInput) =>
      this.put<GoldenLabel>(`/api/v1/golden/labels/${reportId}/${disclosureId}`, body),

    reveal: (id: string) => this.post<GoldenRevealResult>(`/api/v1/golden/reports/${id}/reveal`, {}),

    saveAgreement: (reportId: string, disclosureId: string, body: { system_agreement: 'agree' | 'disagree'; disagreement_reason?: string; compared_evaluation_id?: string }) =>
      this.put<GoldenLabel>(`/api/v1/golden/labels/${reportId}/${disclosureId}/agreement`, body),
  };

  // ============================================
  // BILLING API
  // ============================================

  billing = {
    plans: () => this.get<PlanRecord[]>('/api/v1/billing/plans'),

    subscription: () => this.get<SubscriptionRecord | null>('/api/v1/billing/subscription'),

    checkout: (planCode: PlanCode) =>
      this.post<CheckoutConfig>('/api/v1/billing/checkout', { planCode }),

    activate: (data: { companyId: string; planCode: PlanCode; status?: SubscriptionStatus }) =>
      this.post<{ id: string; status: SubscriptionStatus; plan: PlanCode; provider: string }>(
        '/api/v1/billing/subscriptions/activate', data,
      ),
  };

  // ============================================
  // CONTACT API
  // ============================================

  contact = {
    send: (data: ContactRequest) => this.post<ContactResponse>('/api/v1/contact', data),
  };
}

export const apiClient = new ApiClient(API_BASE_URL);

// Update token when auth changes
export function setApiToken(token: string | null) {
  apiClient.setAccessToken(token);
}

// Export error types for consumer use
export { NetworkError, TimeoutError, ApiClientError };
